/* eslint-disable import/no-unresolved */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { arrayOf, bool, func, node, number, oneOfType, shape, string } from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import { push, replace } from "react-router-redux";
import { withRouter } from "react-router-dom";
import { createPortal } from "react-dom";
import classNames from "classnames";

import {
    EnvironmentSwitcherDrawer,
    PrivateLayout as PrivateLayoutUI,
    ThemeProvider2,
    useIsMobile,
    Box,
} from "@ui-kit/components/index.es";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { selectors as layoutSelectors } from "reducers/layout";
import { actions as notificationActions } from "reducers/notification";
import { actions as sessionActions, selectors as sessionSelectors } from "reducers/session";
import { actions as settingsActions, selectors as settingsSelectors } from "reducers/settings";
import { selectors as communicationsSelectors } from "reducers/communications";
import { actions as debinActions } from "reducers/debin";
import { actions as ecomexActions } from "reducers/ecomex";
import { selectors as loginSelectors } from "reducers/login";
import { selectors as configSelectors } from "reducers/config";
import { ENVIRONMENT_TYPE_CORPORATE } from "constants.js";
import ToastNotification from "v3/components/ToastNotification";
import SessionAboutToExpire from "pages/status/SessionAboutToExpire";
import buildPrivateNavItems, { sectionIdFromPathname } from "v3/layout/buildPrivateNavItems";
import buildFeaturedActionOptions from "v3/layout/buildFeaturedActionOptions";
import {
    getGreetingTexts,
    getHeaderLabels,
    getHeaderUserMenuLabels,
    getMenuLabels,
    getMenuTextParams,
} from "v3/v3UiKitI18n";
import * as i18n from "util/i18n";
import buildEnvironmentSwitcherItems from "util/v3EnvironmentSwitcher";
import { getUnauthenticatedLoginPath, isCredinConfirmationBackToForm } from "util/v3UxConfig";
import firstNameFromFullName from "util/v3UserGreeting";
import { getLastLoginDate, getLastLoginPlace } from "util/settings";
import { isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";
import { getCustomSafeAreaInsets, isLegacyAndroidInset } from "util/safeAreaInsets";

const MENU_RAIL_WIDTH = 124;
const PRIVATE_SHELL_TOAST_TOP_OFFSET_PX_DESKTOP = 94 + 16;
const PRIVATE_SHELL_TOAST_TOP_OFFSET_PX_MOBILE = 64 + 16;

function getPrivateShellToastTopOffsetPx(isMobile) {
    return isMobile ? PRIVATE_SHELL_TOAST_TOP_OFFSET_PX_MOBILE : PRIVATE_SHELL_TOAST_TOP_OFFSET_PX_DESKTOP;
}
/**
 * Misma composición que `pages/settings/Settings.jsx` (fecha + lugar de la sesión anterior).
 *
 * @param {object[]|undefined} previousLoginInfo
 * @returns {string}
 */
function formatUserLastConnection(previousLoginInfo) {
    if (!Array.isArray(previousLoginInfo) || previousLoginInfo.length === 0) {
        return "—";
    }
    const date = getLastLoginDate(previousLoginInfo[0]);
    const place = getLastLoginPlace(previousLoginInfo[0]);
    if (date && place) {
        return `${date} - ${place}`;
    }
    const partial = date || place;
    return partial || "—";
}

/**
 * Mismos enlaces legales que {@link pages/_components/FooterDesktop} (array para `PrivateLayout` del UI kit).
 *
 * @param {import("redux").Dispatch} dispatch
 * @param {string} pathname
 * @returns {Array<{ label: string, onClick: () => void }>}
 */
function getPrivateShellFooterLinks(dispatch, pathname) {
    const goToLegal = (url) => {
        if (pathname === "/termsAndConditions" || pathname === "/support" || pathname === "/privacyPolicy") {
            dispatch(replace(url));
        } else {
            dispatch(push(url));
        }
    };
    return [
        { label: i18n.get("global.privacy"), onClick: () => goToLegal("/privacyPolicy") },
        { label: i18n.get("global.termAndConditions"), onClick: () => goToLegal("/termsAndConditions") },
        { label: i18n.get("global.support"), onClick: () => goToLegal("/support") },
    ];
}

function PrivateLayout({
    children,
    dispatch,
    history,
    location,
    activeEnvironment,
    environments,
    userFirstName,
    userFullName,
    userEmail,
    hideAmounts,
    uxLoginImprovements,
    isRetailEnvironment,
    isAdministrator,
    featureSignatureDispatchControl,
    unreadCommunications,
    sessionUser,
    selectedFeaturedActionId,
    dataAccountsFundcorp,
    goBackPath,
    rightContent,
    titleKey,
    headerTitle,
    headerSubtitle,
    headerSubtitleElement,
    layoutScopes,
}) {
    const { isMobile } = useIsMobile();
    const pathname = location?.pathname ?? "";

    useEffect(() => {
        dispatch(settingsActions.getUserData());
    }, [dispatch]);

    const canOperateFunds = Boolean(activeEnvironment?.permissions?.fundcorpOperations);
    const fundcorpFetched = useRef(false);
    useEffect(() => {
        if (canOperateFunds && !fundcorpFetched.current && !dataAccountsFundcorp?.accountsArray?.length && isMobile) {
            fundcorpFetched.current = true;
            dispatch(accountsActions.listAccountsFundcorp());
        }
    }, [dispatch, dataAccountsFundcorp, canOperateFunds]);

    /** Desktop flyout: only `submenuExpanded` uses this; never derive from URL (user must open/close explicitly). */
    const [selectedSectionId, setSelectedSectionId] = useState("");
    const [environmentDrawerOpen, setEnvironmentDrawerOpen] = useState(false);

    /**
     * Collapse the desktop flyout when the route *section* changes (e.g. transfers → accounts) or
     * when mobile/desktop mapping changes. Intra-section navigation (agenda → historic) does not
     * clear `selectedSectionId`, so an open flyout stays open until the user picks a sub-item or
     * switches section.
     */
    const railSectionSyncRef = useRef(null);
    useEffect(() => {
        const nextSection = sectionIdFromPathname(pathname, isMobile);
        if (railSectionSyncRef.current === null) {
            railSectionSyncRef.current = { pathname, isMobile };
            return;
        }

        const prev = railSectionSyncRef.current;
        const prevSection = sectionIdFromPathname(prev.pathname, prev.isMobile);
        const pathChanged = pathname !== prev.pathname;
        const viewportModeChanged = isMobile !== prev.isMobile;

        if (!pathChanged && !viewportModeChanged) {
            return;
        }

        if (pathChanged && nextSection === prevSection) {
            railSectionSyncRef.current = { pathname, isMobile };
            return;
        }

        setSelectedSectionId("");
        railSectionSyncRef.current = { pathname, isMobile };
    }, [pathname, isMobile]);

    const handleAfterDesktopFlyoutNavigate = useCallback(() => {
        setSelectedSectionId("");
    }, []);

    const environmentName = activeEnvironment?.name ?? "";
    const environmentKeyCount = environments && typeof environments === "object" ? Object.keys(environments).length : 0;
    const hasMultipleEnvironments = environmentKeyCount > 1;
    /** Drawer de ambiente: desktop solo si hay varios; en móvil también con uno (menú contextual). */
    const showEnvironmentSwitcherDrawer = environmentKeyCount > 0 && (hasMultipleEnvironments || isMobile);
    const isCorporateEnvironment = activeEnvironment?.type === ENVIRONMENT_TYPE_CORPORATE;
    const envType = isCorporateEnvironment ? "corporate" : "person";
    /** `activeEnvironment.type === "corporate"` → rail claro; resto → dark (`privatelayout`). */
    const menuPreset = isCorporateEnvironment ? "corporateLight" : "privateBankingDark";

    const handleLogout = useCallback(() => {
        dispatch(sessionActions.logout());
        history.push(getUnauthenticatedLoginPath(uxLoginImprovements));
    }, [dispatch, history, uxLoginImprovements]);

    const handleOpenEnvironmentDrawer = useCallback(() => {
        setEnvironmentDrawerOpen(true);
    }, []);

    const handleCloseEnvironmentDrawer = useCallback(() => {
        setEnvironmentDrawerOpen(false);
    }, []);

    const environmentSwitcherItems = useMemo(() => buildEnvironmentSwitcherItems(environments, activeEnvironment?.id), [
        environments,
        activeEnvironment?.id,
    ]);

    const handleSelectEnvironmentFromDrawer = useCallback(
        (idEnvironment) => {
            setEnvironmentDrawerOpen(false);
            const env = environments && environments[idEnvironment];
            if (!env) {
                return;
            }
            if (env.allowedToAccess === "true") {
                dispatch(
                    sessionActions.changeEnvironment(idEnvironment, false, null, {
                        navigationPath: "/v3/dashboard",
                    }),
                );
            } else {
                const scopesForToast =
                    Array.isArray(layoutScopes) && layoutScopes.length > 0 ? [...layoutScopes] : ["desktop"];
                dispatch(
                    notificationActions.showNotification(
                        i18n.get("administration.restrictions.unavailableEnvironment"),
                        "error",
                        scopesForToast,
                    ),
                );
                dispatch(push("/v3/dashboard"));
            }
        },
        [dispatch, environments, layoutScopes],
    );

    const goToMessages = useCallback(() => {
        dispatch(push("/communications"));
    }, [dispatch]);

    const handleHideAmounts = useCallback(() => {
        dispatch(settingsActions.updateHideBalance(!hideAmounts));
    }, [dispatch, hideAmounts]);

    const menuItems = useMemo(
        () =>
            buildPrivateNavItems({
                selectedSectionId,
                setSelectedSectionId,
                dispatch,
                history,
                pathname,
                activeEnvironment,
                isRetailEnvironment,
                isAdministrator,
                isMobile,
                featureSignatureDispatchControl,
                onLogout: handleLogout,
            }),
        [
            selectedSectionId,
            dispatch,
            history,
            pathname,
            activeEnvironment,
            isRetailEnvironment,
            isAdministrator,
            isMobile,
            featureSignatureDispatchControl,
            handleLogout,
        ],
    );

    const userLastConnection = useMemo(() => formatUserLastConnection(sessionUser?.previousLoginInfo), [sessionUser]);

    const greetingFirstName = useMemo(() => firstNameFromFullName(userFullName) || userFirstName || "", [
        userFullName,
        userFirstName,
    ]);

    const mobileBottomNavOrderIds = useMemo(() => {
        if (!isMobile) {
            return undefined;
        }
        const ids = ["home", "token"];
        if (!isRetailEnvironment) {
            if (menuItems.some((it) => it.id === "investments")) {
                ids.push("investments");
            }
        } else if (menuItems.some((it) => it.id === "metrocorp")) {
            ids.push("metrocorp");
        }
        return ids;
    }, [isMobile, isRetailEnvironment, menuItems]);

    const fundcorpAccountsCount = dataAccountsFundcorp?.accountsArray?.length || 0;

    const featuredActionOptions = useMemo(() => {
        if (!isMobile) {
            return undefined;
        }
        return buildFeaturedActionOptions({
            dispatch,
            activeEnvironment,
            isRetailEnvironment,
            featureSignatureDispatchControl,
            fundcorpAccountsCount,
            currentPathname: pathname || "/v3/dashboard",
        });
    }, [
        isMobile,
        dispatch,
        activeEnvironment,
        isRetailEnvironment,
        featureSignatureDispatchControl,
        fundcorpAccountsCount,
        pathname,
    ]);

    const effectiveFeaturedActionId = useMemo(() => {
        if (selectedFeaturedActionId) {
            return selectedFeaturedActionId;
        }
        return isRetailEnvironment ? "transfer" : "pendingDispatch";
    }, [selectedFeaturedActionId, isRetailEnvironment]);

    const handleFeaturedActionChange = useCallback(
        (id) => {
            dispatch(settingsActions.setFeaturedActionId(id));
        },
        [dispatch],
    );

    const handleFeaturedActionClick = useCallback(
        (id) => {
            if (!featuredActionOptions) {
                return;
            }
            const opt = featuredActionOptions.find((o) => o.id === id);
            if (opt && opt.onClick) {
                opt.onClick();
            }
        },
        [featuredActionOptions],
    );

    const goToConsolidatedPosition = useCallback(() => {
        dispatch(push("/ConsolidatedPosition"));
    }, [dispatch]);

    const mobileContextMenuOptions = useMemo(() => {
        if (!isMobile || environmentKeyCount === 0) {
            return undefined;
        }
        return [
            {
                icon: envType === "corporate" ? "Business" : "User",
                iconVariant: "outline",
                label: environmentName || "—",
                tagText: "Cambiar",
                callback: () => {
                    if (showEnvironmentSwitcherDrawer) {
                        handleOpenEnvironmentDrawer();
                    }
                },
            },
            {
                icon: "Survey",
                iconVariant: "outline",
                label: i18n.get("desktop.consolidatedPosition.label", "Consolidated position"),
                callback: () => {
                    goToConsolidatedPosition();
                },
            },
        ];
    }, [
        isMobile,
        environmentKeyCount,
        envType,
        environmentName,
        showEnvironmentSwitcherDrawer,
        handleOpenEnvironmentDrawer,
        goToConsolidatedPosition,
    ]);

    const footerLinks = useMemo(() => getPrivateShellFooterLinks(dispatch, pathname), [dispatch, pathname]);

    /**
     * Rutas donde lista y detalle comparten pathname: `push(goBackPath)` no provoca navegación real y no
     * limpia el estado local (Redux); se despacha el cierre explícito del detalle.
     */
    const handleGoBack = useCallback(() => {
        if (!goBackPath) {
            return;
        }
        if (goBackPath === pathname) {
            if (pathname === "/ecomex/consultOperations") {
                dispatch(ecomexActions.closeDetail());
                return;
            }
            if (pathname === "/ecomex/informativeRegime") {
                dispatch(ecomexActions.closeDetailInformativeRegime());
                return;
            }
        }
        if (isCredinConfirmationBackToForm(pathname, goBackPath)) {
            dispatch(debinActions.setIsBackFromSummary(goBackPath));
            return;
        }
        dispatch(push(goBackPath));
    }, [dispatch, goBackPath, pathname]);

    const headerProps = useMemo(
        () => ({
            enviromentType: envType,
            environmentName,
            userName: greetingFirstName || "—",
            firstName: greetingFirstName,
            userFullName: userFullName || "",
            userEmail: userEmail || undefined,
            userLastConnection,
            menuRailWidthPx: MENU_RAIL_WIDTH,
            hideAmounts: Boolean(hideAmounts),
            hasUnreadMessages: Number(unreadCommunications) > 0,
            getGreetingTexts,
            getHeaderLabels,
            getHeaderUserMenuLabels,
            handleChangeEnvironment: hasMultipleEnvironments ? handleOpenEnvironmentDrawer : () => undefined,
            environmentButtonDisabled: !hasMultipleEnvironments,
            handleHideAmounts,
            goToMessages,
            goToConfig: () => dispatch(push("/settings")),
            goToAccountOfficial: () => dispatch(push("/accountsOfficer")),
            handleLogout,
            mobileContextMenuOptions,
            mobileContextMenuSheetTitle: getHeaderLabels("header.mobileContextMenu.sheetTitle"),
            showAsStandadPage: isMobile && !pathname.includes("/v3/dashboard"),
            goBack: goBackPath ? handleGoBack : undefined,
            handleGoToHome: () => dispatch(push("/v3/dashboard")),
            title: headerTitle || (titleKey ? i18n.get(titleKey) : undefined),
            subtitle: headerSubtitle || undefined,
            subtitleElement: headerSubtitleElement,
            rightContent,
        }),
        [
            envType,
            environmentName,
            greetingFirstName,
            userEmail,
            userFullName,
            userLastConnection,
            hideAmounts,
            unreadCommunications,
            dispatch,
            goToMessages,
            handleLogout,
            hasMultipleEnvironments,
            handleOpenEnvironmentDrawer,
            handleHideAmounts,
            mobileContextMenuOptions,
            pathname,
            isMobile,
            goBackPath,
            handleGoBack,
            titleKey,
            headerTitle,
            headerSubtitle,
            headerSubtitleElement,
            rightContent,
        ],
    );

    const customSafeAreaInsets = getCustomSafeAreaInsets();

    return (
        <ThemeProvider2
            isV3Layout={isUxDashboardImprovementsEnabled()}
            customSafeAreaInsetTop={customSafeAreaInsets.top}
            customSafeAreaInsetBottom={customSafeAreaInsets.bottom}>
            <ToastNotification topOffsetPx={getPrivateShellToastTopOffsetPx(isMobile)} />
            {/* Portal a body: SessionAboutToExpire renderiza `.modal-container` (flex-grow:1) que,
                    dentro del layout flex v3, estiraba el contenido. Fuera del arbol del shell no afecta
                    scroll ni espaciado; el Modal ya se portalea a body al mostrarse. */}
            {createPortal(<SessionAboutToExpire location={location} isDesktop={!isMobile} />, document.body)}
            <Box
                className={classNames("private-layout", {
                    "legacy-android-inset": isLegacyAndroidInset(),
                })}>
                <PrivateLayoutUI
                    menuPreset={menuPreset}
                    menuRailWidthPx={MENU_RAIL_WIDTH}
                    menuProps={{
                        getMenuLabels,
                        getMenuTextParams,
                        logoIcon: "Menu2",
                        version: window.appVersion,
                        items: menuItems,
                        mobileBottomNavOrderIds,
                        mainAccessMobileOptions: featuredActionOptions,
                        selectedMainAccessMobileId: effectiveFeaturedActionId,
                        onMainAccessMobileChange: handleFeaturedActionChange,
                        onMainAccessMobileClick: handleFeaturedActionClick,
                        onAfterDesktopFlyoutNavigate: handleAfterDesktopFlyoutNavigate,
                        onDesktopFlyoutOutsidePointerDown: handleAfterDesktopFlyoutNavigate,
                        onDismissExpandedMobileSubmenu: handleAfterDesktopFlyoutNavigate,
                    }}
                    headerProps={headerProps}
                    staticsColors={!pathname.includes("/v3/dashboard")}
                    footerLinks={footerLinks}
                    isV1={!pathname.includes("/v2") && !pathname.includes("/v3")}>
                    {children}
                </PrivateLayoutUI>
                {showEnvironmentSwitcherDrawer ? (
                    <EnvironmentSwitcherDrawer
                        isOpen={environmentDrawerOpen}
                        onClose={handleCloseEnvironmentDrawer}
                        items={environmentSwitcherItems}
                        onSelectEnvironment={handleSelectEnvironmentFromDrawer}
                        title={i18n.get("settings.changeEnvironment.title.desktop")}
                        searchPlaceholder={i18n.get("settings.changeEnvironment.search.placeholder")}
                    />
                ) : null}
            </Box>
        </ThemeProvider2>
    );
}

PrivateLayout.propTypes = {
    children: node,
    dispatch: func.isRequired,
    history: shape({ push: func.isRequired }).isRequired,
    location: shape({ pathname: string }).isRequired,
    activeEnvironment: shape({ name: string, type: string, id: number, permissions: shape({}) }),
    environments: shape({}),
    userFirstName: string,
    userFullName: string,
    userEmail: string,
    hideAmounts: bool,
    uxLoginImprovements: bool,
    isRetailEnvironment: bool,
    isAdministrator: bool,
    featureSignatureDispatchControl: bool,
    unreadCommunications: number,
    sessionUser: shape({
        previousLoginInfo: oneOfType([arrayOf(shape({})), shape({})]),
    }),
    selectedFeaturedActionId: string,
    dataAccountsFundcorp: shape({}),
    goBackPath: string,
    rightContent: node,
    titleKey: string,
    headerTitle: string,
    headerSubtitle: string,
    headerSubtitleElement: node,
    layoutScopes: arrayOf(string),
};

PrivateLayout.defaultProps = {
    children: null,
    activeEnvironment: null,
    environments: null,
    userFirstName: "",
    userFullName: "",
    userEmail: "",
    hideAmounts: false,
    uxLoginImprovements: false,
    isRetailEnvironment: false,
    isAdministrator: false,
    featureSignatureDispatchControl: false,
    unreadCommunications: 0,
    sessionUser: null,
    selectedFeaturedActionId: null,
    dataAccountsFundcorp: {},
    goBackPath: null,
    rightContent: null,
    titleKey: null,
    headerTitle: "",
    headerSubtitle: "",
    headerSubtitleElement: null,
    layoutScopes: [],
};

const mapStateToProps = (state) => ({
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    environments: sessionSelectors.getEnvironments(state),
    userFirstName: loginSelectors.getUserFirstName(state),
    userFullName: sessionSelectors.getUserFullName(state),
    userEmail: sessionSelectors.getUsername(state),
    sessionUser: sessionSelectors.getUser(state),
    hideAmounts: settingsSelectors.getUserData(state)?.hideBalance,
    uxLoginImprovements: !!configSelectors.getConfigKey(state, "frontend.UXImprovements.Login.functionalities"),
    isRetailEnvironment: sessionSelectors.isRetailEnvironment(state),
    isAdministrator: sessionSelectors.isAdministrator(state),
    featureSignatureDispatchControl: !!configSelectors.getConfigKey(state, "feature.signatureSchema.dispatchControl"),
    unreadCommunications: communicationsSelectors.getUnreadCommunications(state),
    selectedFeaturedActionId: settingsSelectors.getSelectedFeaturedActionId(state),
    dataAccountsFundcorp: accountsSelectors.getDataAccountsFundcorp(state),
    goBackPath: layoutSelectors.getGoBackPath(state),
    rightContent: layoutSelectors.getRightContentMobile(state),
    titleKey: layoutSelectors.getTitleKey(state),
    headerTitle: layoutSelectors.getHeaderTitle(state),
    headerSubtitle: layoutSelectors.getHeaderSubtitle(state),
    headerSubtitleElement: layoutSelectors.getHeaderSubtitleElement(state),
    layoutScopes: layoutSelectors.getScopes(state),
});

export default compose(withRouter, connect(mapStateToProps))(PrivateLayout);
